import Head from 'next/head';
import type { GetStaticProps, GetStaticPaths, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../../_app';
import React from 'react';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { getPageContentContentApi } from '@/lib/service/home';
import { getTdkApi } from '@/lib/service/tdk';
import { faqJsonLd, page3JsonLd } from '@/lib/common/jsonld';
import { hyphenatedToTitleCase, removeLastPathSegment } from '@/shared/path-helper';
import { useRouter } from 'next/router';
import { ITab } from '@/components/shadowban-test/indexMain';
import { getFaqList } from '@/utils/landingData';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';

const ShadowbanTest = dynamic(() => import('@/components/shadowban-test/indexMain'), {
  ssr: true,
});

// ⭐ ISR: 预生成 Shadowban 测试页面路径（只预生成英文，其他语言通过 Fallback 生成）
export const getStaticPaths: GetStaticPaths = async () => {
  const paths: { params: { type: string }; locale: string }[] = [];

  // 3 个固定的平台类型
  const types = ['twitter-shadowban-test', 'instagram-shadowban-test', 'tiktok-shadowban-test'];

  // 只预生成英文页面
  types.forEach((type) => {
    paths.push({
      params: { type },
      locale: 'en',
    });
  });

  return {
    paths,
    fallback: 'blocking', // 其他语言和新类型首次访问时生成
  };
};

// ⭐ ISR: 转换为静态生成 + 1 小时重新验证
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale, params } = context;
  const resolvedUrl = `/shadowban-test/${params.type}`;

  // ⭐ ISR 调试日志（删除时搜索 isrGenerated）
  console.log(
    `[ISR] 🔄 Regenerating page: ${resolvedUrl} | Locale: ${locale} | Time: ${new Date().toLocaleString('zh-CN')}`,
  );

  const [localeRes, tdkData, res] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl, locale),
    getPageContentContentApi(`/shadowban-test/${params?.type}`, locale, '', ''),
  ]);

  // 404 处理
  if (res.code != 0) {
    return {
      notFound: true,
    };
  }

  let currentURL = `${process.env.NEXT_APP_WEB_HOST}${resolvedUrl}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}${resolvedUrl}`;
  }

  const parentPath = removeLastPathSegment(resolvedUrl);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;
  const parentUrl = siteUrl + parentPath;
  const parentTitle = hyphenatedToTitleCase(parentPath);
  const pageTitle = tdkData?.data?.title ?? tempData[`${params?.type}-title`] ?? null;
  const pageDescription = tdkData?.data?.description ?? tempData[`${params?.type}-desc`] ?? null;

  const jsonLd = page3JsonLd(
    {
      title: pageTitle,
      desc: pageDescription,
      url: currentURL,
    },
    {
      title: parentTitle,
      url: parentUrl,
    },
    {
      url: siteUrl,
    },
  );

  const faqList = getFaqList(res);

  const faqJSONLD = faqJsonLd(
    {
      title: hyphenatedToTitleCase(resolvedUrl),
      desc: pageDescription,
      url: currentURL,
    },
    {
      url: siteUrl,
    },
    faqList,
  );

  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      title: pageTitle,
      description: pageDescription,
      data: res.data,
      jsonLd: jsonLd,
      faqJSONLd: faqJSONLD,
      type: params?.type,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME, // 1 小时自动重新验证
  };
};

const Home: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: any) => {
  const { asPath } = useRouter();
  // 获取当前路径的type，如果是 twitter-shadowban-test ，则defaultTab为 'twitter'
  // 获取当前路径的type，如果是 instagram-shadowban-test ，则defaultTab为 'instagram'
  // 获取当前路径的type，如果是 tiktok-shadowban-test ，则defaultTab为 'tiktok'
  let defaultTab = '';
  if (props.type.includes('twitter-shadowban-test')) {
    defaultTab = 'twitter';
  } else if (props.type.includes('instagram-shadowban-test')) {
    defaultTab = 'instagram';
  } else if (props.type.includes('tiktok-shadowban-test')) {
    defaultTab = 'tiktok';
  }
  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName="Shadowban Test 子页面"
        additionalInfo={{
          语言: props.locale,
          '平台类型': props.type,
        }}
      />
      <ShadowbanTest key={asPath} content={props.data} defaultTab={defaultTab as ITab['tab']} isSubPage={props.type} />
    </>
  );
};
Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const jsonLd = page?.props?.jsonLd;
  const faqJSONLd = page?.props?.faqJSONLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta
            name="viewport"
            content="width=minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />

          <link
            rel="alternate"
            hrefLang="en"
            href={`${process.env.NEXT_APP_WEB_HOST}/en/shadowban-test/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="zh"
            href={`${process.env.NEXT_APP_WEB_HOST}/zh/shadowban-test/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="tw"
            href={`${process.env.NEXT_APP_WEB_HOST}/tw/shadowban-test/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="ja"
            href={`${process.env.NEXT_APP_WEB_HOST}/ja/shadowban-test/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="ko"
            href={`${process.env.NEXT_APP_WEB_HOST}/ko/shadowban-test/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="pt"
            href={`${process.env.NEXT_APP_WEB_HOST}/pt/shadowban-test/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="de"
            href={`${process.env.NEXT_APP_WEB_HOST}/de/shadowban-test/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="es"
            href={`${process.env.NEXT_APP_WEB_HOST}/es/shadowban-test/${page.props.type}`}
          />
          <link
            rel="alternate"
            hrefLang="it"
            href={`${process.env.NEXT_APP_WEB_HOST}/it/shadowban-test/${page.props.type}`}
          />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
          {faqJSONLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJSONLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
